import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import UserDatabases from '@Services/UserDatabases';
import StructureService from '@Services/Structure/StructureService';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';
import Catalyst from '@Services/Catalyst';
import CUSTOMER_DEPARTMENT_IDS_AND_ICONS_MAPPING, { CUSTOMER_LIBRARY_NAMES_AND_ICONS_MAPPING } from './CustomerIconConstants';

/**
 * generate customer icon arial label value
 * @param customerLabel
 * @returns {string}
 */
export function getCustomerIconAriaLabel(customerLabel) {
  return `Copy SMILES from ${customerLabel}`;
}

/**
 * get mapped icon name and label
 * @param departmentId
 * @returns label & svg icon name object
 */
export function getCustomerSpecificMapping(departmentId) {
  return CUSTOMER_DEPARTMENT_IDS_AND_ICONS_MAPPING[departmentId];
}

/**
 * Resolves customer icon metadata from the optional libraryNames field, falling
 * back to the existing department mapping. Tooltip names preserve backend order.
 * @param customerInfo
 * @returns customer icon metadata with tooltipLabel, or undefined
 */
export function getCustomerIconMapping(customerInfo = {}) {
  const { libraryNames, name } = customerInfo;
  const validLibraryNames = Array.isArray(libraryNames)
    ? libraryNames.filter((libraryName) => typeof libraryName === 'string' && libraryName.length > 0)
    : [];
  const libraryMapping = validLibraryNames.length === 1 ? CUSTOMER_LIBRARY_NAMES_AND_ICONS_MAPPING[validLibraryNames[0]] : undefined;
  const customerMapping = libraryMapping || getCustomerSpecificMapping(name);

  if (!customerMapping) return undefined;

  return {
    ...customerMapping,
    tooltipLabel: validLibraryNames.length > 0 ? validLibraryNames.join(', ') : customerMapping.label,
  };
}

/**
 * checks if needed to render customer specific icon in structure toolbar
 * @param customerInfo
 * @returns true if should be rendered
 */
export function isCustomerIconVisible(customerInfo) {
  return !!(customerInfo?.showIcon && getCustomerIconMapping(customerInfo));
}

/**
 * function to load and copy structure smiles to clipboard
 * @param structure
 * @returns {Promise<unknown>}
 */
export function copySmiles(structure) {
  return StructureService.convertToSmiles(structure)
    .then((smiles) => {
      navigator.clipboard.writeText(smiles.structure);
      showCopiedToastMessage('The SMILES string of the structure has been copied to the clipboard.');
    })
    .catch(Logger.error);
}

export function trackCustomerIconClick(customerName) {
  if (!customerName) return;
  Catalyst.trackEvent('buttonClick', {
    buttonType: `menu:${customerName.toLowerCase()}`, // v124
  });
}

export function canUseStructureAsFilter({ structure, metadata }) {
  const { databaseId, context, fromDocuments, fromRT, fromSynthesisPlanner, isViewPreview } = metadata;
  const hasStructure = !!structure;
  const isTargets = context === 'tgitems';
  const isStructureFilterHidden = UserDatabases.isRCSDatabase(databaseId) && Auth.isFeatureEnabled(FEATURES.hideRCSStructureFilter);

  return hasStructure && !isViewPreview && !fromDocuments && !isTargets && !fromSynthesisPlanner && !fromRT && !isStructureFilterHidden;
}
